import React, { useState } from "react";
import { CustomerAdd } from "./CustomerAdd";
import { CustomerRow } from "./CustomerRow";
import { CustomerTableHeader } from "./CustomerTableHeader";
import { CustomerListHeader } from "./CustomerListHeader";
import { EmptyState } from "./EmptyState";
import { LoadingSpinner } from "./LoadingSpinner";
import { formatDate } from "../../utils/date";
import { formatPhoneNumber } from "../../utils/number";
import { useCustomers } from "../../hooks/useCustomers";
import { Customer } from "../../services/customers";

export const getStatusColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case "processed":
      return "bg-green-100 text-green-800 border-green-200";
    case "unprocessed":
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

export const List: React.FC = () => {
  const [showAddForm, setShowAddForm] = useState(false);

  const {
    customers,
    loading,
    updatingStatus,
    editingStatus,
    deletingCustomer,
    showDeleteConfirm,
    handleStatusUpdate,
    toggleStatusEdit,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    refreshCustomers,
  } = useCustomers();

  const handleCustomerAdded = () => {
    setShowAddForm(false);
    refreshCustomers();
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
  };

  const handleAddCustomer = () => {
    setShowAddForm(true);
  };

  if (loading) {
    return <LoadingSpinner size="lg" text="Loading customers..." />;
  }

  if (showAddForm) {
    return (
      <CustomerAdd
        onCustomerAdded={handleCustomerAdded}
        onCancel={handleCancelAdd}
      />
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <CustomerListHeader
          customerCount={customers.length}
          onAddCustomer={handleAddCustomer}
        />

        {customers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <CustomerTableHeader />
              <tbody className="bg-white divide-y divide-gray-200">
                {customers.map((customer: Customer) => (
                  <CustomerRow
                    key={customer.id}
                    customer={customer}
                    updatingStatus={updatingStatus}
                    editingStatus={editingStatus}
                    deletingCustomer={deletingCustomer}
                    showDeleteConfirm={showDeleteConfirm}
                    onStatusUpdate={handleStatusUpdate}
                    onToggleStatusEdit={toggleStatusEdit}
                    onDeleteClick={handleDeleteClick}
                    onDeleteConfirm={handleDeleteConfirm}
                    onDeleteCancel={handleDeleteCancel}
                    formatDate={formatDate}
                    formatPhoneNumber={formatPhoneNumber}
                    getStatusColor={getStatusColor}
                  />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState onAddCustomer={handleAddCustomer} />
        )}
      </div>
    </div>
  );
};
